/* eslint-disable no-restricted-syntax */
/**
 * MedicalProviderCard - Display card for Finch MedicalProvider entities
 *
 * Styled to match FilevineEntityCard with a preview and Details link
 * that navigates to the treatment detail page.
 */

import { ExternalLink, Mail, Phone, User } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ContactHandleSchema } from '@/services/api/entitySyncService';
import type {
  AddressContactHandleSchema,
  MedicalProviderEntitySchema,
} from '@/services/api/generated/entitySyncService';

interface MedicalProviderCardProps {
  provider: MedicalProviderEntitySchema;
  matterId?: string;
  className?: string;
  compact?: boolean;
}

const addressToString = (handle: AddressContactHandleSchema): string => {
  const lines: string[] = [];
  lines.push(handle.line1);
  if (handle.line2) {
    lines.push(handle.line2);
  }
  if (handle.line3) {
    lines.push(handle.line3);
  }
  lines.push(`${handle.city}, ${handle.state} ${handle.zip}`);
  if (handle.country) {
    lines.push(handle.country);
  }
  return lines.filter(Boolean).join('\n');
};

/**
 * Extract contact info from handles
 */
function getContactInfo(handles: ContactHandleSchema[] | undefined): {
  phone: string | null;
  email: string | null;
  fax: string | null;
  address: string | null;
} {
  let phone: string | null = null;
  let email: string | null = null;
  let fax: string | null = null;
  let address: string | null = null;

  for (const handle of handles ?? []) {
    switch (handle.handle_type) {
      case 'PHONE':
        if (!phone) phone = handle.handle_value ?? null;
        break;
      case 'EMAIL':
        if (!email) email = handle.handle_value ?? null;
        break;
      case 'FAX':
        if (!fax) fax = handle.handle_value ?? null;
        break;
      case 'ADDRESS':
        if (!address) address = addressToString(handle);
        break;
      default:
        break;
    }
  }

  return { phone, email, fax, address };
}

/**
 * Get status badge color
 */
function getStatusColor(status: string): string {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('active') || statusLower.includes('current')) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (statusLower.includes('complete') || statusLower.includes('discharged')) {
    return 'bg-blue-100 text-blue-700';
  }
  if (statusLower.includes('pending') || statusLower.includes('scheduled')) {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-zinc-100 text-zinc-700';
}

export const MedicalProviderCard: React.FC<MedicalProviderCardProps> = ({
  provider,
  matterId,
  className,
  compact = false,
}) => {
  const contactInfo = getContactInfo(provider.handles);

  // Build the treatment detail URL
  const detailUrl = matterId
    ? `/matters/${matterId}/treatment/${provider.id}`
    : null;

  return (
    <div
      className={cn(
        'flex rounded-lg border border-zinc-200 bg-white',
        compact ? 'p-2' : 'p-3',
        className,
      )}
    >
      {/* Left: Preview content aligned to top */}
      <div className="flex-1 space-y-1 text-sm">
        {/* Name with icon */}
        <div className="flex items-center gap-1.5 text-zinc-900 font-medium">
          <User className="h-3.5 w-3.5 text-zinc-500" />
          {provider.name}
        </div>

        {/* Contact info - indented to align with name text */}
        {contactInfo.phone && (
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 pl-5">
            <Phone className="h-3 w-3" />
            {contactInfo.phone}
          </div>
        )}
        {contactInfo.email && (
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 pl-5">
            <Mail className="h-3 w-3" />
            {contactInfo.email}
          </div>
        )}

        {/* Status badge inline with content */}
        {provider.status && (
          <Badge
            variant="secondary"
            className={cn('text-xs', getStatusColor(provider.status))}
          >
            {provider.status}
          </Badge>
        )}
      </div>

      {/* Right: Details link aligned to bottom */}
      {detailUrl && (
        <div className="flex shrink-0 flex-col justify-end">
          <Link
            to={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-6 items-center gap-1 px-2 text-xs text-zinc-500 hover:text-zinc-700"
          >
            <ExternalLink className="h-3 w-3" />
            Details
          </Link>
        </div>
      )}
    </div>
  );
};

export default MedicalProviderCard;
